<template>
  <AppShell>
    <section class="view on soa-view-plan" aria-labelledby="plan-title">
      <div class="pagehead">
        <div>
          <h1 id="plan-title">{{ t("plan_h") }}</h1>
          <p class="sub">{{ t("plan_sub") }}</p>
        </div>
      </div>

      <!-- GEO_EXEC_R18 · six-state recommendation rows. Off (404) → the section is absent.
           Outside the loading/empty chain on purpose: a brand whose 90-day plan is empty can still
           have recommendations, and they are the thing to do next. -->
      <RecommendationList />

      <p v-if="loading" class="empty" role="status">{{ t("loading") }}</p>
      <p v-else-if="error" class="empty" role="alert">
        {{ error }}
        <button type="button" class="atbtn" @click="load">{{ t("plan_retry") }}</button>
      </p>
      <p v-else-if="!tasks.length" class="empty">{{ t("plan_empty") }}</p>
      <div v-else :aria-busy="busy ? 'true' : 'false'">
        <section v-if="proposed.length" class="pl-sec" aria-labelledby="plan-proposed-heading">
          <h2 id="plan-proposed-heading" class="pl-h pl-warn">{{ format("plan_proposed", { count: proposed.length }) }}</h2>
          <button type="button" class="pl-go plan-approve" :disabled="busy" @click="approve">
            {{ busyAction === "approve" ? t("plan_approving") : t("plan_approve") }}
          </button>
          <PlanTaskCard v-for="task in proposed" :key="task.id" :task="task" />
        </section>

        <section class="pl-sec" aria-labelledby="plan-current-heading">
          <h2 id="plan-current-heading" class="pl-h">{{ format("plan_this_week", { week: weekNow }) }}</h2>
          <template v-if="current.length">
            <PlanTaskCard v-for="task in current" :key="task.id" :task="task" />
          </template>
          <p v-else class="pl-empty">{{ t("plan_week_clear") }}</p>
        </section>

        <section v-if="waiting.length" class="pl-sec" aria-labelledby="plan-waiting-heading">
          <h2 id="plan-waiting-heading" class="pl-h pl-warn">{{ t("plan_waiting") }}</h2>
          <PlanTaskCard v-for="task in waiting" :key="task.id" :task="task" />
        </section>

        <section v-if="verifying.length" class="pl-sec" aria-labelledby="plan-verifying-heading">
          <h2 id="plan-verifying-heading" class="pl-h">{{ format("plan_verifying_group", { count: verifying.length }) }}</h2>
          <PlanTaskCard v-for="task in verifying" :key="task.id" :task="task" />
        </section>

        <section v-if="later.length" class="pl-sec" aria-labelledby="plan-later-heading">
          <h2 id="plan-later-heading" class="pl-h">{{ t("plan_later") }}</h2>
          <PlanTaskCard v-for="task in later" :key="task.id" :task="task" />
        </section>

        <section v-if="completed.length" class="pl-sec" aria-labelledby="plan-completed-heading">
          <h2 id="plan-completed-heading" class="pl-h">{{ format("plan_verified_group", { count: completed.length }) }}</h2>
          <PlanTaskCard v-for="task in completed" :key="task.id" :task="task" />
        </section>
      </div>
      <p class="sr-only" role="status" aria-live="polite">{{ status }}</p>
    </section>
  </AppShell>
</template>

<script setup>
import { computed, defineComponent, h, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";

import { approvePlan, getPlan, provideTask } from "../api/plans";
import RecommendationList from "../components/RecommendationList.vue";
import AppShell from "../layout/AppShell.vue";
import { currentBrand } from "../stores/brands";
import {
  onPlanExecutionCompleted,
  planExecutionFailureForTask,
  planExecutionForTask,
  submitPlanExecution,
} from "../stores/planExecutions.js";
import { useI18n } from "../useI18n";

const data = ref({ tasks: [], week_now: 1 });
const loading = ref(true);
const error = ref("");
const busyAction = ref("");
const status = ref("");
const clock = ref(Date.now());
const router = useRouter();
const { lang, t } = useI18n();
let controller;
let clockTimer;
let unsubscribeExecution;

function normalizePlan(payload) {
  if (!payload || typeof payload !== "object") return { tasks: [], week_now: 1 };
  return { ...payload, tasks: Array.isArray(payload.tasks) ? payload.tasks : [], week_now: payload.week_now || 1 };
}

const busy = computed(() => Boolean(busyAction.value));
function executionFor(task) {
  return planExecutionForTask(task?.id);
}

function failureFor(task) {
  return planExecutionFailureForTask(task?.id);
}

function taskStatus(task) {
  const failure = failureFor(task);
  if (failure && task.status === "active" && ["proposed", "planned"].includes(failure.origin_status)) {
    return failure.origin_status;
  }
  return task.owner === "maya" && executionFor(task) && task.status !== "done" ? "active" : task.status;
}

const tasks = computed(() => (data.value.tasks || []).filter((task) => {
  const selected = currentBrand.value;
  if (!selected) return true;
  if (task.brand_id != null && selected.id != null) return String(task.brand_id) === String(selected.id);
  return String(task.brand || "").toLowerCase() === String(selected.brand || "").toLowerCase();
}));
const weekNow = computed(() => data.value.week_now || 1);
const proposed = computed(() => tasks.value.filter((task) => taskStatus(task) === "proposed"));
const current = computed(() => tasks.value.filter((task) => task.owner === "maya" && !["dropped", "proposed"].includes(taskStatus(task)) && ((task.week <= weekNow.value && taskStatus(task) !== "done") || (taskStatus(task) === "done" && Date.now() / 1000 - (task.updated || 0) < 7 * 86400))));
const waiting = computed(() => tasks.value.filter((task) => task.owner === "client" && !["done", "verifying", "dropped", "proposed"].includes(taskStatus(task))));
const verifying = computed(() => tasks.value.filter((task) => taskStatus(task) === "verifying"));
const later = computed(() => tasks.value.filter((task) => task.owner === "maya" && task.week > weekNow.value && !["done", "dropped", "proposed"].includes(taskStatus(task))));
const currentIds = computed(() => new Set(current.value.map((task) => task.id)));
const completed = computed(() => tasks.value.filter((task) => taskStatus(task) === "done" && !currentIds.value.has(task.id)));

function format(key, values) {
  return Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`{${name}}`, String(value)), t(key));
}

function taskLabel(task, key) {
  return task[`${key}_${lang.value}`] || task[key] || "";
}

function taskExecutionLabel(execution) {
  return !execution || execution.state === "queued" ? t("plan_queued") : t("plan_running");
}

async function execute(task) {
  busyAction.value = `execute-${task.id}`;
  error.value = "";
  const previousStatus = task.status;
  task.status = "active";
  task.updated = Date.now() / 1000;
  try {
    await submitPlanExecution(task.id);
    status.value = t("plan_started");
  } catch (caught) {
    task.status = previousStatus;
    error.value = caught.message || t("plan_execute_error");
  } finally {
    busyAction.value = "";
  }
}

function elapsed(task) {
  const execution = executionFor(task);
  const started = Number(
    execution?.submittedAt
      || (execution?.started_at || execution?.created || task.updated || 0) * 1000,
  );
  const seconds = Math.max(0, Math.floor((clock.value - started) / 1000));
  return format("plan_elapsed", { seconds });
}

async function provide(task) {
  busyAction.value = `provide-${task.id}`;
  error.value = "";
  try {
    const result = await provideTask(task.id, lang.value);
    status.value = result.msg || t("plan_materials_requested");
    await router.push({ path: "/maya", query: result.task_wb ? { task: String(task.id) } : {} });
  } catch (caught) {
    error.value = caught.message || t("plan_provide_error");
  } finally {
    busyAction.value = "";
  }
}

const PlanTaskCard = defineComponent({
  name: "PlanTaskCard",
  props: { task: { type: Object, required: true } },
  setup(props) {
    return () => {
      const task = props.task;
      const execution = executionFor(task);
      const starting = busyAction.value === `execute-${task.id}`;
      const active = Boolean(execution) || starting;
      const failed = Boolean(failureFor(task));
      const effectiveStatus = taskStatus(task);
      const mayaActionable = task.owner === "maya" && (active || ["proposed", "planned"].includes(effectiveStatus));
      const action = mayaActionable
        ? h("button", {
          type: "button",
          class: "pl-go",
          disabled: active,
          "aria-busy": active ? "true" : undefined,
          onClick: active ? undefined : () => execute(task),
        }, active ? taskExecutionLabel(execution) : failed ? t("plan_retry") : t("plan_do_now"))
        : task.owner === "client" && !["done", "verifying"].includes(effectiveStatus)
          ? h("button", { type: "button", class: "pl-go", disabled: busyAction.value === `provide-${task.id}`, onClick: () => provide(task) }, busyAction.value === `provide-${task.id}` ? t("plan_opening") : t("plan_provide"))
          : null;
      const failure = failureFor(task);
      const state = failed
        ? h("span", { class: "pl-failed", role: "alert" }, failure?.reason || failure?.message || t("plan_execute_error"))
        : effectiveStatus === "done"
        ? task.deliv_slug
          ? h("button", {
            type: "button",
            class: "pl-donebadge pl-rev",
            onClick: () => router.push({ path: "/maya", query: { deliverable: task.deliv_slug } }),
          }, `✓ ${t("plan_verified")} · ${t("wbr_open")}`)
          : h("span", { class: "pl-donebadge" }, `✓ ${t("plan_verified")}`)
        : effectiveStatus === "verifying"
          ? h("span", { class: "pl-verify" }, `✓ ${t("plan_verifying")}`)
          : active && task.owner === "maya"
            ? h("div", { class: "pl-progress", role: "status", "aria-live": "polite", "aria-label": taskExecutionLabel(execution) }, [
              h("span", { class: "pl-spinner", "aria-hidden": "true" }),
              h("span", { class: "pl-progress-label" }, taskExecutionLabel(execution)),
              h("span", { class: "pl-elapsed", "aria-hidden": "true" }, elapsed(task)),
              h("span", { class: "pl-progress-track", "aria-hidden": "true" }, [h("span")]),
            ])
            : null;
      return h("article", { class: ["pl-card", { isdone: effectiveStatus === "done", "is-active": active }], "aria-busy": active ? "true" : "false" }, [
        h("div", { class: "pl-top" }, [
          h("span", { class: "pl-wk" }, `W${task.week}`),
          h("h3", taskLabel(task, "title")),
          h("span", { class: ["pl-chip", task.owner === "maya" ? "pl-m" : "pl-c"] }, task.owner === "maya" ? "Maya" : t("plan_you")),
          h("span", { class: "pl-brand" }, task.brand),
        ]),
        h("p", { class: "pl-why" }, taskLabel(task, "why")),
        h("div", { class: "pl-acts" }, [action, state]),
      ]);
    };
  },
});

async function load(options = {}) {
  const silent = options?.silent === true;
  controller?.abort();
  controller = new AbortController();
  if (!silent) {
    loading.value = true;
    error.value = "";
  }
  try {
    data.value = normalizePlan(await getPlan({ signal: controller.signal }));
  } catch (caught) {
    if (caught.name !== "AbortError") error.value = caught.message || t("plan_load_error");
  } finally {
    if (!silent) loading.value = false;
  }
}

async function approve() {
  busyAction.value = "approve";
  error.value = "";
  try {
    await approvePlan(currentBrand.value?.id);
    status.value = t("plan_approved");
    await load();
  } catch (caught) {
    error.value = caught.message || t("plan_approve_error");
  } finally {
    busyAction.value = "";
  }
}

onMounted(() => {
  document.title = `${t("plan_page_title")} · SoaRank`;
  load();
  clockTimer = window.setInterval(() => { clock.value = Date.now(); }, 1000);
  unsubscribeExecution = onPlanExecutionCompleted((result) => {
    status.value = result.msg || t("plan_completed");
    void load({ silent: true });
  });
});
watch(lang, () => { document.title = `${t("plan_page_title")} · SoaRank`; });
watch(() => currentBrand.value?.id, load);
onBeforeUnmount(() => {
  controller?.abort();
  window.clearInterval(clockTimer);
  unsubscribeExecution?.();
});
</script>

<style scoped>
.soa-view-plan .pagehead h1 {
  margin-bottom: 8px;
}

.soa-view-plan .pl-top h3 {
  margin: 0;
  font-size: inherit;
  font-weight: 700;
}

.plan-approve {
  margin-bottom: 12px;
}

.pl-empty {
  color: var(--mute);
  font-size: var(--fs-subhead);
}

.soa-view-plan .pl-warn,
.soa-view-plan :deep(.pl-c) {
  color: #b3341f;
}

.pl-go:disabled {
  cursor: wait;
  opacity: 0.65;
}

.pl-failed {
  color: #b3341f;
  font-size: var(--fs-caption);
  font-weight: 650;
}

.soa-view-plan .pl-card.is-active {
  border-color: color-mix(in srgb, var(--indigo) 35%, var(--line));
}

.soa-view-plan .pl-card.isdone {
  opacity: 1;
}

.pl-progress {
  display: grid;
  grid-template-columns: 16px auto 1fr;
  align-items: center;
  gap: 8px;
  width: min(100%, 360px);
  color: var(--indigo);
  font-size: var(--fs-caption);
  font-weight: 700;
}

.pl-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid color-mix(in srgb, var(--indigo) 22%, transparent);
  border-top-color: var(--indigo);
  border-radius: 50%;
  animation: plan-spin 0.8s linear infinite;
}

.pl-elapsed {
  justify-self: end;
  color: var(--mute);
  font-weight: 500;
  font-variant-numeric: tabular-nums;
}

.pl-progress-track {
  grid-column: 1 / -1;
  position: relative;
  height: 3px;
  overflow: hidden;
  border-radius: 2px;
  background: color-mix(in srgb, var(--indigo) 12%, transparent);
}

.pl-progress-track span {
  position: absolute;
  inset-block: 0;
  width: 38%;
  border-radius: inherit;
  background: var(--indigo);
  animation: plan-progress 1.5s ease-in-out infinite;
}

@keyframes plan-spin {
  to { transform: rotate(360deg); }
}

@keyframes plan-progress {
  from { transform: translateX(-110%); }
  to { transform: translateX(365%); }
}

@media (prefers-reduced-motion: reduce) {
  .pl-spinner,
  .pl-progress-track span {
    animation: none;
  }

  .pl-progress-track span {
    width: 100%;
    opacity: 0.55;
  }
}

@media (prefers-color-scheme: dark) {
  .soa-view-plan .pl-warn,
  .soa-view-plan :deep(.pl-c) {
    color: var(--verm);
  }
}
</style>
